import { get } from 'lodash'
import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import SessionAuth from './SessionAuth'
import storeAuth from './StoreAuth'

// RemoveSession(true) clears the whole of localStorage (SessionAuth.ResetSessionStorage), and App writes
// these three keys before this ever settles. Losing currentLanguage would put an Arabic customer back on
// the default language just because a token went stale, so they are carried across the reset.
const BOOT_KEYS = ['currentLanguage', 'currentCountry', 'siteVersion']

const ClearDeadSession = async () => {
    const kept = BOOT_KEYS.map(key => [key, localStorage.getItem(key)])

    try {
        await SessionAuth.RemoveSession(true)
    } catch (error) {
        console.error('could not clear the stored session', error)
    }

    kept.forEach(([key, value]) => {
        if (value !== null) {
            localStorage.setItem(key, value)
        }
    })
}

// A refresh sits on top of a session that has already restored, so a failure here must leave the customer
// logged in with the token they arrived with.
const RefreshStoredToken = async () => {
    try {
        const response = await SessionAuth.CheckToken(
            SessionAuth.GetLoginExtend('token'),
            SessionAuth.GetLoginExtend('country'),
            SessionAuth.GetLoginExtend('id')
        )

        const token = get(response, 'data.token')

        if (token) {
            SessionAuth.SetLoginExtend('token', token)
        }
    } catch (error) {
        console.error('could not refresh the stored token', error)
    }
}

/**
 * Restores the stored session on start-up and falls back to a clean logged-out state when it cannot.
 *
 * Replaces App.checkToken, where the recovery was unreachable: its .catch() rethrew inside the promise
 * chain, so the surrounding try/catch never saw the failure, SessionAuth.RemoveSession(true) never ran,
 * and a stale token left the app half-restored (on dev the rejection stopped it before any API call).
 *
 * Never rejects.
 *
 * @returns {Promise<boolean>} true when the session was restored
 */
export const RestoreSessionOnBoot = async () => {
    if (!SessionAuth.GetLoginExtend('token')) {
        StoreLoader.SetApiLoaded('refreshToken', true)
        return false
    }

    try {
        // The resolved value is not a signal. getCustomerMenu already returns response.data and
        // RestoreSession returns response.data again, so a successful restore resolves undefined —
        // which is why the old `if (response)` never let the refresh below run. A throw is the failure.
        await storeAuth.RestoreSession(SessionAuth.GetLoginExtends()[StoreCountry.CountryLowerCase()])
    } catch (error) {
        await ClearDeadSession()

        return false
    } finally {
        // Has to run on every path. StoreLoader.GetPercentSummary() divides by the number of keys in
        // mainApiLoaded, so once this key exists, a path that skips it holds IsWebReady() under 100%
        // and the customer waits on the LoadingBar for good.
        StoreLoader.SetApiLoaded('refreshToken', true)
    }

    await RefreshStoredToken()

    return true
}
